import {
  PolygonLayer,
  LayerEvent,
  MapScene,
  Control,
  Popup,
  ColorComponent,
} from "@antv/l7-react";
import React, { useEffect, useState } from "react";
import { usePapaParse } from "react-papaparse";
import { Box } from "@mui/system";
import DownloadButton from "../Common/DownloadButton";
import ModalTrigger from "../Common/ModalTrigger";
import classes from "../../Style/ReportingCountry.scss";
import MapTooltip from "./MapTooltip";

const ReportingCountryMap = (props) => {
  const samplesFilter = props.samplesFilter;
  const negatives = samplesFilter.indexOf("Difference") > -1;

  const [hovered, setHovered] = useState(undefined);
  const csv = props.csv;
  const { readString } = usePapaParse();
  const json = props.json;
  const [max, setMax] = useState(undefined);
  const [min, setMin] = useState(undefined);

  const initialState = {
    json: null,
    conv: null,
  };
  const [data, setData] = useState(initialState);

  const handleReadString = () => {
    readString(csv, {
      complete: (results, file) => {
        json.features.map((j) => {
          const found = results.data.find(
            (d) => d["Country of sampling"] === j.properties["NAME"]
          );
          if (found) {
            j.properties = { ...j.properties, sum: found[samplesFilter] };
          } else {
            j.properties = { ...j.properties, sum: 0 };
          }
          return j;
        });
        if (chart) {
          chart.layer.setData(json);
        }
        setData({ json: json, conv: results.data });
        setMax(
          Math.round(
            Math.max(
              ...results.data.map((f) =>
                f[samplesFilter] ? f[samplesFilter] : 0
              )
            )
          )
        );
        setMin(
          Math.round(
            Math.min(
              ...results.data.map((f) =>
                f[samplesFilter] ? f[samplesFilter] : 0
              )
            )
          )
        );
      },
      download: true,
      header: true,
      dynamicTyping: true,
      error: (error, file) => {
        console.log("Error while parsing:", error, file);
      },
    });
  };

  useEffect(() => {
    handleReadString();
  }, [samplesFilter]);

  const [chart, setChart] = useState(undefined);

  const handleLoaded = (scene, layer) => {
    setChart({ scene, layer });
  };

  const [canvas, setCanvas] = useState(undefined);

  const getTooltipContent = (country) => {
    const found = data.conv.find((d) => d["Country of sampling"] === country);
    if (!found) {
      return <p>{country}</p>;
    }

    return (
      <div className="tooltipWrapper" style={{ wordWrap: "break-word" }}>
        {samplesFilter && (
          <>
            <span id="repCountrytooltip">
              <b>Reporting country: {found["Country of sampling"]}</b>
            </span>
            <br></br>
            <span
              id="repCountrytooltip2"
              style={{
                color: "#22294D",
                paddingBottom: "0px",
                fontSize: "12px",
              }}
            >
              {found[samplesFilter] === null
                ? samplesFilter + ": " + "-"
                : samplesFilter +
                  ": " +
                  (samplesFilter === "Number of samples" ||
                  samplesFilter === "Number of samples per 100k inhabitants"
                    ? found[samplesFilter].toLocaleString()
                    : Math.round(found[samplesFilter] * 10) / 10)}{" "}
              {samplesFilter === "Number of samples" ||
              samplesFilter === "Number of samples per 100k inhabitants"
                ? ""
                : "%"}
            </span>
          </>
        )}
      </div>
    );
  };
  let colors = [
    "#fff",
    "#D0D1D5",
    "#BDBFC6",
    "#9699A7",
    "#9699A7",
    "#484D6A",
    "#22294D",
  ];
  if (negatives) {
    colors = [
      "#fff",
      "#f9b11233",
      "#ffe7a6",
      "#f9b11299",
      "#ffcd42",
      "#ffc629",
      "#F9B112",
      "#D0D1D5",
      "#BDBFC6",
      "#9699A7",
      "#9699A7",
      "#484D6A",
      "#22294D",
    ];
  }

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 900);
    }
    window.addEventListener("resize", handleResize);
    return (_) => {
      window.removeEventListener("resize", handleResize);
    };
  });
  useEffect(() => {
    const canvases = document.getElementsByClassName("l7-marker-container");
    for (let i = 0; i < canvases.length; i++) {
      canvases[i].onwheel = function (event) {
        event.stopPropagation();
      };
    }
  });

  const createLegendLabel = (reduction, isNegative) => {
    if (isNegative) {
      if (min) {
        return (samplesFilter.indexOf("Difference") > -1 ||
          samplesFilter.indexOf("Rate")) > -1
          ? Math.round(min * reduction) + "%"
          : Math.round(min * reduction);
      }
    } else {
      if (max) {
        return (samplesFilter.indexOf("Difference") > -1 ||
          samplesFilter.indexOf("Rate")) > -1
          ? Math.round(max * reduction) + "%"
          : Math.round(max * reduction);
      }
    }
    return "";
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end">
        <Box display="flex" flexDirection="column">
          {data.conv && !negatives && (
            <div
              className="legent-title"
              style={{
                fontFamily: "Roboto Condensed, sans-serif",
                fontSize: isMobile ? "12px" : "15px",
                width: isMobile ? "250px" : "300px",
                float: "right",
              }}
            >
              <ColorComponent
                items={[
                  { title: 0, color: "#D0D1D5" },
                  {
                    title: createLegendLabel(0.1).toLocaleString(),
                    color: "#BDBFC6",
                  },
                  {
                    title: createLegendLabel(0.3).toLocaleString(),
                    color: "#9699A7",
                  },
                  {
                    title: createLegendLabel(0.5).toLocaleString(),
                    color: "#6F7389",
                  },
                  {
                    title: createLegendLabel(0.6).toLocaleString(),
                    color: "#484D6A",
                  },
                  {
                    title: createLegendLabel(1).toLocaleString(),
                    color: "#22294D",
                  },
                ]}
              />
            </div>
          )}
          {negatives && min && (
            <div
              className="legent-title"
              style={{
                fontFamily: "Roboto Condensed, sans-serif",
                fontSize: isMobile ? "12px" : "15px",
                width: isMobile ? "250px" : "300px",
                float: "right",
              }}
            >
              <ColorComponent
                items={[
                  {
                    title:
                      "-" + createLegendLabel(1, false, true).toLocaleString(),
                    color: "#F9B112",
                  },
                  {
                    title:
                      "-" +
                      createLegendLabel(0.6, false, true).toLocaleString(),
                    color: "#f9b11299",
                  },
                  {
                    title:
                      "-" +
                      createLegendLabel(0.2, false, true).toLocaleString(),
                    color: "#f9b11233",
                  },
                  { title: 0, color: "#D0D1D5" },
                  {
                    title: createLegendLabel(0.2).toLocaleString(),
                    color: "#6F7389",
                  },
                  {
                    title: createLegendLabel(0.6).toLocaleString(),
                    color: "#484D6A",
                  },
                  {
                    title: createLegendLabel(1).toLocaleString(),
                    color: "#22294D",
                  },
                ]}
              />
            </div>
          )}
        </Box>
        <Box display="flex" pl="10px">
          <DownloadButton
            isMap
            canvas={canvas}
            fileName="Map"
            csv={data.conv}
          />
          <Box pl="10px">
            <ModalTrigger
              style={{ color: "#22294D", cursor: "pointer" }}
              text=""
            />
          </Box>
        </Box>
      </Box>
      {data.json && (
        <MapScene
          onSceneLoaded={(scene) => {
            setCanvas(scene);
          }}
          autoFit
          map={{
            pitch: 0,
            style: "blank",
            zoom: 2,
            minZoom: 2,
            maxZoom: 4,
            center: [112, 30],
            renderer: "svg",
          }}
          style={{
            height: "500px",
            position: "relative",
          }}
        >
          <PolygonLayer
            onLayerLoaded={(layer, scene) => {
              handleLoaded(scene, layer);
            }}
            key={1}
            zIndex={1}
            source={{
              data: data.json,
            }}
            color={{
              field: "sum",
              values: (d) => {
                if (negatives) {
                  if (d === 0 || d === null) {
                    return colors[0];
                  } else if (d < 0 && d >= min * 0.1) {
                    return colors[1];
                  } else if (d < min * 0.1 && d >= min * 0.3) {
                    return colors[3];
                  } else if (d < min * 0.3 && d >= min * 0.5) {
                    return colors[4];
                  } else if (d < min * 0.5 && d >= min * 0.6) {
                    return colors[5];
                  } else if (d < min * 0.6 && d >= min) {
                    return colors[6];
                  } else if (d > 0 && d <= max * 0.1) {
                    return colors[7];
                  } else if (d > max * 0.1 && d <= max * 0.3) {
                    return colors[8];
                  } else if (d > max * 0.3 && d <= max * 0.5) {
                    return colors[9];
                  } else if (d > max * 0.5 && d <= max * 0.6) {
                    return colors[10];
                  } else {
                    return colors[11];
                  }
                } else {
                  if (d === 0 || d === null) {
                    return colors[0];
                  } else if (d > 0 && d <= max * 0.1) {
                    return colors[1];
                  } else if (d > max * 0.1 && d <= max * 0.3) {
                    return colors[3];
                  } else if (d > max * 0.3 && d <= max * 0.5) {
                    return colors[4];
                  } else if (d > max * 0.5 && d <= max * 0.6) {
                    return colors[5];
                  } else {
                    return colors[6];
                  }
                }
              },
            }}
            options={{
              autoFit: true,
            }}
            size={{
              values: 1,
            }}
            shape={{ values: "fill" }}
            active={{
              option: {
                color: "#C6C2C2", //on hover
              },
            }}
            select={false}
          >
            <LayerEvent
              type="mouseenter"
              handler={(e) => {
                setHovered(e);
              }}
            ></LayerEvent>
            <LayerEvent
              type="mouseout"
              handler={(e) => {
                setHovered(undefined);
              }}
            ></LayerEvent>
          </PolygonLayer>

          <PolygonLayer
            key={2}
            source={{
              data: data.json,
            }}
            size={{
              values: 0.5,
            }}
            color={{
              values: "#fff", //line between
            }}
            shape={{
              values: "line",
            }}
            style={{
              opacity: 0.5,
            }}
            zIndex={10}
          />

          {hovered &&
            samplesFilter &&
            data.conv.find(
              (d) =>
                d["Country of sampling"] === hovered.feature.properties.NAME
            ) && (
            <MapTooltip>
                {getTooltipContent(hovered.feature.properties.NAME)}
            </MapTooltip>
            )}

          <Control type="scale" position="bottomright" />
          <Control type="zoom" position="bottomright" />
        </MapScene>
      )}
    </Box>
  );
};

export default ReportingCountryMap;
